import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    AlertTriangle,
    Users,
    TrendingUp,
    Globe,
    Calendar,
    Trash2,
    Eye,
    LogOut,
    RefreshCcw,
    Compass,
    Skull,
} from "lucide-react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    LineChart,
    Line,
    Cell,
} from "recharts";
import { toast } from "sonner";
import SEO from "@/components/common/SEO";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const COLORS = ["#D32F2F", "#FFB300", "#e8e6e1", "#a8a098", "#FF5722", "#8b1a1a"];

function StatBlock({ icon: Icon, label, value, sub, accent = "danger" }) {
    const accentClass =
        accent === "warning"
            ? "text-[var(--warning)]"
            : accent === "paper"
              ? "text-[var(--paper-2)]"
              : "text-[var(--danger)]";
    return (
        <div className="border border-[var(--line)] bg-[#0d0d0d] p-6">
            <div className="flex items-center justify-between mb-4">
                <Icon className={`w-6 h-6 ${accentClass}`} />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-dim)]">
                    {sub || ""}
                </span>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] mb-2">
                {label}
            </p>
            <p className={`font-display text-5xl leading-none ${accentClass}`}>
                {value}
            </p>
        </div>
    );
}

function ChartCard({ title, code, children }) {
    return (
        <div className="dossier">
            <div className="dossier-header">
                <span>{code} · {title}</span>
                <span className="text-[var(--danger)]">Agregado</span>
            </div>
            <div className="p-5 md:p-7">{children}</div>
        </div>
    );
}

const tooltipStyle = {
    background: "#111",
    border: "1px solid #2a2a2a",
    color: "#e8e6e1",
    fontFamily: "IBM Plex Mono, monospace",
    fontSize: 12,
};

function SessionDetailModal({ session, onClose }) {
    if (!session) return null;
    return (
        <div
            className="fixed inset-0 z-[80] flex items-start justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={onClose}
            data-testid="session-detail-modal"
        >
            <div
                className="relative max-w-3xl w-full bg-[#0d0d0d] border-2 border-[var(--danger)] my-8"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="bg-[var(--danger)] text-[var(--paper-2)] px-5 py-3 flex justify-between items-center">
                    <span className="font-mono text-xs uppercase tracking-[0.25em]">
                        Expediente · {session.id?.slice(0, 8).toUpperCase()}
                    </span>
                    <button
                        onClick={onClose}
                        className="font-mono text-xs underline"
                        data-testid="detail-close"
                    >
                        Cerrar
                    </button>
                </div>
                <div className="p-6 max-h-[80vh] overflow-y-auto">
                    <div className="grid sm:grid-cols-3 gap-4 mb-6 pb-4 border-b border-dashed border-[var(--ink-dim)]">
                        <Pair k="Nombre" v={session.name} />
                        <Pair k="Email" v={session.email} />
                        <Pair k="País" v={session.country} />
                        <Pair k="Fecha" v={(session.created_at || "").slice(0, 19).replace("T", " ")} />
                        <Pair k="Crisis" v={session.crisis_flagged ? "SÍ" : "No"} danger={session.crisis_flagged} />
                        <Pair k="Consent estudios" v={session.consent_to_research ? "Sí" : "No"} />
                    </div>
                    <h3 className="font-display text-2xl text-[var(--danger)] mb-3">
                        Respuestas
                    </h3>
                    <pre className="font-mono text-[11px] text-[var(--paper)] bg-[#111] border border-[var(--line)] p-4 whitespace-pre-wrap break-words leading-relaxed">
                        {JSON.stringify(session.answers, null, 2)}
                    </pre>
                </div>
            </div>
        </div>
    );
}

function Pair({ k, v, danger }) {
    return (
        <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-dim)] mb-1">
                {k}
            </p>
            <p className={`font-mono text-sm ${danger ? "text-[var(--danger)] font-bold" : "text-[var(--paper-2)]"} break-all`}>
                {v || "—"}
            </p>
        </div>
    );
}

export default function AdminMapaInverso() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [filterCrisis, setFilterCrisis] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [s, l] = await Promise.all([
                api.get("/admin/mapa-inverso/stats"),
                api.get("/admin/mapa-inverso?limit=200"),
            ]);
            setStats(s.data);
            setSessions(l.data.items || []);
            setTotal(l.data.total || 0);
        } catch {
            toast.error("No se pudieron cargar las métricas.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    async function handleLogout() {
        await logout();
        navigate("/login");
    }

    async function remove(id) {
        if (!confirm("¿Eliminar esta sesión permanentemente?")) return;
        try {
            await api.delete(`/admin/mapa-inverso/${id}`);
            toast.success("Sesión eliminada.");
            await reload();
        } catch {
            toast.error("Error al eliminar.");
        }
    }

    async function openDetail(id) {
        try {
            const { data } = await api.get(`/admin/mapa-inverso/${id}`);
            setSelected(data);
        } catch {
            toast.error("No se pudo cargar el detalle.");
        }
    }

    const filteredSessions = useMemo(() => {
        return filterCrisis
            ? sessions.filter((s) => s.crisis_flagged)
            : sessions;
    }, [sessions, filterCrisis]);

    const dailyData = useMemo(() => {
        if (!stats?.daily) return [];
        return stats.daily.map((d) => ({
            day: d.day.slice(5), // mm-dd
            count: d.count,
        }));
    }, [stats]);

    return (
        <>
            <SEO
                title="Admin · Mapa Inverso"
                description="Métricas del Mapa Inverso"
                path="/admin/mapa-inverso"
            />
            <section className="py-12 md:py-16" data-testid="admin-mapa-inverso">
                <div className="container-x">
                    {/* Header */}
                    <div className="flex flex-wrap justify-between items-start gap-4 mb-10 pb-8 border-b border-[var(--line)]">
                        <div>
                            <Link
                                to="/admin"
                                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] hover:text-[var(--danger)] mb-4"
                                data-testid="admin-back-to-hub"
                            >
                                <ArrowLeft className="w-3 h-3" />
                                Volver al panel
                            </Link>
                            <p className="eyebrow mb-3">Departamento · Mapa Inverso</p>
                            <h1 className="font-display text-4xl md:text-6xl text-[var(--paper-2)] leading-[0.95]">
                                Métricas <span className="text-[var(--danger)]">Mapa Inverso</span>
                            </h1>
                            <p className="font-mono text-xs text-[var(--ink-muted)] mt-2">
                                Sesión: {user?.email} · {total} expedientes en archivo
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={reload}
                                className="btn-brutal btn-brutal-ghost"
                                data-testid="reload-stats"
                                disabled={loading}
                            >
                                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                                Recargar
                            </button>
                            <button
                                onClick={handleLogout}
                                className="btn-brutal btn-brutal-ghost"
                                data-testid="admin-logout"
                            >
                                <LogOut className="w-4 h-4" />
                                Salir
                            </button>
                        </div>
                    </div>

                    {/* KPI grid */}
                    {stats && (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="kpi-grid">
                            <StatBlock
                                icon={Users}
                                label="Sesiones totales"
                                value={stats.total_sessions}
                                accent="paper"
                            />
                            <StatBlock
                                icon={Calendar}
                                label="Últimos 30 días"
                                value={stats.last_30_days}
                                accent="warning"
                            />
                            <StatBlock
                                icon={AlertTriangle}
                                label="Marcadas crisis"
                                value={stats.crisis_flagged}
                                accent="danger"
                                sub="señal de riesgo"
                            />
                            <StatBlock
                                icon={Globe}
                                label="Países distintos"
                                value={stats.by_country.length}
                                accent="paper"
                            />
                        </div>
                    )}

                    {/* Charts */}
                    {stats && (
                        <div className="grid lg:grid-cols-2 gap-6 mb-12" data-testid="charts">
                            <ChartCard title="Sesiones por día · 30d" code="01">
                                <div style={{ width: "100%", height: 240 }}>
                                    <ResponsiveContainer>
                                        <LineChart data={dailyData}>
                                            <CartesianGrid stroke="#2a2a2a" strokeDasharray="3 3" />
                                            <XAxis dataKey="day" stroke="#a8a098" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                                            <YAxis stroke="#a8a098" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} allowDecimals={false} />
                                            <Tooltip contentStyle={tooltipStyle} />
                                            <Line
                                                type="monotone"
                                                dataKey="count"
                                                stroke="#D32F2F"
                                                strokeWidth={2}
                                                dot={{ fill: "#FFB300", r: 3 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </ChartCard>

                            <ChartCard title="Top sustancias / conductas" code="02">
                                <div style={{ width: "100%", height: 240 }}>
                                    <ResponsiveContainer>
                                        <BarChart data={stats.by_sustancia} layout="vertical">
                                            <CartesianGrid stroke="#2a2a2a" strokeDasharray="3 3" />
                                            <XAxis type="number" stroke="#a8a098" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} allowDecimals={false} />
                                            <YAxis dataKey="name" type="category" stroke="#a8a098" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} width={120} />
                                            <Tooltip contentStyle={tooltipStyle} />
                                            <Bar dataKey="count">
                                                {stats.by_sustancia.map((_, i) => (
                                                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </ChartCard>

                            <ChartCard title="Países" code="03">
                                <div style={{ width: "100%", height: 240 }}>
                                    <ResponsiveContainer>
                                        <BarChart data={stats.by_country}>
                                            <CartesianGrid stroke="#2a2a2a" strokeDasharray="3 3" />
                                            <XAxis dataKey="name" stroke="#a8a098" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                                            <YAxis stroke="#a8a098" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} allowDecimals={false} />
                                            <Tooltip contentStyle={tooltipStyle} />
                                            <Bar dataKey="count" fill="#FFB300" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </ChartCard>

                            <ChartCard title="Emoción dominante" code="04">
                                <div style={{ width: "100%", height: 240 }}>
                                    <ResponsiveContainer>
                                        <BarChart data={stats.by_emocion}>
                                            <CartesianGrid stroke="#2a2a2a" strokeDasharray="3 3" />
                                            <XAxis dataKey="name" stroke="#a8a098" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                                            <YAxis stroke="#a8a098" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} allowDecimals={false} />
                                            <Tooltip contentStyle={tooltipStyle} />
                                            <Bar dataKey="count" fill="#D32F2F" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </ChartCard>

                            <ChartCard title="Frecuencia de consumo" code="05">
                                <div style={{ width: "100%", height: 240 }}>
                                    <ResponsiveContainer>
                                        <BarChart data={stats.by_frecuencia} layout="vertical">
                                            <CartesianGrid stroke="#2a2a2a" strokeDasharray="3 3" />
                                            <XAxis type="number" stroke="#a8a098" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} allowDecimals={false} />
                                            <YAxis dataKey="name" type="category" stroke="#a8a098" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} width={160} />
                                            <Tooltip contentStyle={tooltipStyle} />
                                            <Bar dataKey="count" fill="#e8e6e1" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </ChartCard>

                            <ChartCard title="Salida vs Infierno" code="06">
                                <div className="grid grid-cols-2 gap-3 py-4">
                                    <div className="border-2 border-[var(--danger)] p-4 text-center">
                                        <Skull className="w-8 h-8 text-[var(--danger)] mx-auto mb-2" />
                                        <p className="font-display text-3xl text-[var(--danger)]">
                                            {stats.total_sessions}
                                        </p>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mt-1">
                                            Rutas dibujadas
                                        </p>
                                    </div>
                                    <div className="border-2 border-[var(--warning)] p-4 text-center">
                                        <Compass className="w-8 h-8 text-[var(--warning)] mx-auto mb-2" />
                                        <p className="font-display text-3xl text-[var(--warning)]">
                                            {stats.total_sessions}
                                        </p>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mt-1">
                                            Mapas de salida
                                        </p>
                                    </div>
                                </div>
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-dim)] text-center">
                                    Cada expediente genera ambas rutas
                                </p>
                            </ChartCard>
                        </div>
                    )}

                    {/* Sessions list */}
                    <div className="mt-12">
                        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
                            <h2 className="font-display text-3xl text-[var(--paper-2)]">
                                Expedientes recientes
                            </h2>
                            <label className="flex items-center gap-2 font-mono text-xs text-[var(--paper-2)]">
                                <input
                                    type="checkbox"
                                    checked={filterCrisis}
                                    onChange={(e) => setFilterCrisis(e.target.checked)}
                                    className="w-4 h-4 accent-[var(--danger)]"
                                    data-testid="filter-crisis-toggle"
                                />
                                Solo crisis
                            </label>
                        </div>

                        <div className="border border-[var(--line)] overflow-x-auto" data-testid="sessions-table">
                            <table className="w-full">
                                <thead className="bg-[var(--paper-2)] text-black">
                                    <tr className="font-mono text-[10px] uppercase tracking-[0.2em]">
                                        <th className="text-left p-3">Fecha</th>
                                        <th className="text-left p-3">Nombre</th>
                                        <th className="text-left p-3">Email</th>
                                        <th className="text-left p-3">País</th>
                                        <th className="text-left p-3">Sustancia</th>
                                        <th className="text-left p-3">Crisis</th>
                                        <th className="text-right p-3">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredSessions.map((s) => (
                                        <tr
                                            key={s.id}
                                            className="border-t border-[var(--line)] hover:bg-[#1a1a1a]"
                                            data-testid={`session-row-${s.id}`}
                                        >
                                            <td className="p-3 font-mono text-xs text-[var(--ink-muted)] whitespace-nowrap">
                                                {(s.created_at || "").slice(0, 16).replace("T", " ")}
                                            </td>
                                            <td className="p-3 font-mono text-sm text-[var(--paper-2)]">
                                                {s.name}
                                            </td>
                                            <td className="p-3 font-mono text-xs text-[var(--paper)]">
                                                {s.email}
                                            </td>
                                            <td className="p-3 font-mono text-xs text-[var(--paper)]">
                                                {s.country}
                                            </td>
                                            <td className="p-3 font-mono text-xs text-[var(--paper)]">
                                                {s.answers?.starting?.sustancia || "—"}
                                            </td>
                                            <td className="p-3">
                                                {s.crisis_flagged ? (
                                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--danger)] border border-[var(--danger)] px-2 py-1">
                                                        SÍ
                                                    </span>
                                                ) : (
                                                    <span className="font-mono text-[10px] text-[var(--ink-dim)]">
                                                        —
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-3 text-right">
                                                <div className="inline-flex gap-2">
                                                    <button
                                                        onClick={() => openDetail(s.id)}
                                                        className="p-2 border border-[var(--line)] text-[var(--paper-2)] hover:border-[var(--warning)] hover:text-[var(--warning)]"
                                                        data-testid={`view-${s.id}`}
                                                        title="Ver detalle"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => remove(s.id)}
                                                        className="p-2 border border-[var(--danger)] text-[var(--danger)] hover:bg-[var(--danger)] hover:text-[var(--paper-2)]"
                                                        data-testid={`delete-${s.id}`}
                                                        title="Eliminar"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredSessions.length === 0 && (
                                        <tr>
                                            <td colSpan={7} className="p-10 text-center font-mono text-sm text-[var(--ink-muted)]">
                                                {loading
                                                    ? "Cargando expedientes…"
                                                    : "Sin expedientes en archivo."}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </section>

            <SessionDetailModal
                session={selected}
                onClose={() => setSelected(null)}
            />
        </>
    );
}
